import React, { useState, useEffect, useRef, useContext } from 'react';
import { toast } from 'material-react-toastify';
import AuthApis from '../helper/api.service';
import LoginForm from './LoginForm';
import OTPModal from './OtpModal';
import TermsModal from './TermsModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import useIsMobile from '../hooks/useIsMobile';
import OtpSheet from './OtpSheet';
import { UserContext } from '../context/UserContext';

const Login = ({ customDetails, history }) => {
	const isMobile = useIsMobile();
	const { setUserId, setIsFIP, isFip, setConsentIdList, setConsentIdFromLogin } = useContext(UserContext);

	const queryParams = new URLSearchParams(window.location.search);
	const ecid = queryParams.get('ecid') || null;
	const ecreq = queryParams.get('ecreq') || null;
	const reqDate = queryParams.get('reqdate') || null;
	const fi = queryParams.get('fi') || null;
	const entity = queryParams.get('entity') || null;
	
	if (ecid) {
		global.ecid = ecid;
	}
    
	if (entity) {
		setIsFIP(true);
	}

	const [state, setState] = useState({
		time: 60,
		OTP: '',
		mobileNumber: '',
		AAHandle: '',
		isResend: true,
		isOpenSheet: false,
		isOpenOTPSheet: false,
		isOpenPrivacySheet: false,
		disableType: '',
		verifySpinner: false,
		sendSpinner: false,
		consentIdList: [],
	});

	// Refs
	const intervalRef = useRef(null);
	const authApisRef = useRef(new AuthApis());

	let path = '';
	if (ecid) {
		path = 'revoke-consent';
	} else {
		path = '/entity-selection';
	}

    useEffect(() => {
        const init = async () => {
            if (window.finvuClient) {
                await window.finvuClient.openRevoke();
            }
            
            if (ecreq && reqDate && fi) {
                try {
                    const res = await authApisRef.current.userIdFromConsentData(ecreq, reqDate, fi, null);
                    if (res?.status === 'SUCCESS') {
                        updateState({
                            mobileNumber: res?.mobileNumber,
                            consentIdList: res?.consentIdList,
                        });
                        setConsentIdList(res?.consentIdList);
                        setConsentIdFromLogin(true);
                        sendOtp(res?.mobileNumber);
                    }
                } catch (error) {
                    console.error('Error fetching user ID from consent data:', error);
                    toast.error('Failed to fetch user details');
                }
            }
        };
    	init();
    }, []);

	// Timer effect
	useEffect(() => {
		if (!state.isResend && state.time > 0) {
			intervalRef.current = setInterval(() => {
				setState((prev) => ({
					...prev,
					time: prev.time - 1,
				}));
			}, 1000);
		} else if (state.time === 0) {
			clearInterval(intervalRef.current);
			setState((prev) => ({
				...prev,
				time: 60,
				isResend: true,
			}));
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [state.isResend, state.time]);

	// Helper function to update state
	const updateState = (updates) => {
		setState((prev) => ({ ...prev, ...updates }));
	};
    
	// Business logic functions
	const sendOtp = async (mobileNumberParam) => {
		updateState({
			isResend: false,
			OTP: '',
			sendSpinner: true,
			time: 60,
		});

		let aaHandle = state.AAHandle;
		const mobile = (typeof mobileNumberParam === 'string' || typeof mobileNumberParam === 'number')
        ? mobileNumberParam 
        : state.mobileNumber;
		
		if (aaHandle) {
			aaHandle = aaHandle.replace('@finvu', '');
			aaHandle = `${aaHandle}@finvu`;
		}
		try {
			const res = await authApisRef.current.userLogin(
				mobile,
				aaHandle
			);
			if (res && res?.status === 'SEND') {
				updateState({
					isOpenOTPSheet: true,
					sendSpinner: false,
				});
			} else {
				updateState({ sendSpinner: false });
				toast.error(res.message);
			}
		} catch (error) {
			updateState({ sendSpinner: false });
			toast.error('An error occurred while sending OTP');
			console.error(error);
		}
	};

	const generateToast = (res) => {
		if (res.UserInfo.userId) {
			setUserId(res.UserInfo.userId);
		}
		toast.success(`Welcome! Your AA handle is ${res.UserInfo.userId}`);
	};

	const verifyOtp = async () => {
		updateState({
			isResend: false,
			OTP: '',
			verifySpinner: true,
			time: 60,
		});

		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}

		try {
			const res = await authApisRef.current.verifyOtp(state.OTP);

			updateState({
				verifySpinner: false,
				time: 60,
				isResend: true,
			});

			if (res?.status && res?.status === 'SUCCESS') {
				authApisRef.current.getUserDetails(generateToast);

				history.push({
					pathname: `/entity-selection`,
					state: {
						number: state.mobileNumber,
						consentIdList: state.consentIdList,
					},
				});
			} else {
				toast.error(res?.message);
			}
		} catch (error) {
			updateState({
				verifySpinner: false,
				time: 60,
				isResend: true,
			});
			toast.error('An error occurred while verifying OTP');
			console.error(error);
		}
	};

	// Event handlers
	const handleMobileNumberChange = (value) => {
		updateState({ mobileNumber: value, AAHandle: '' });
	};

	const handleAAHandleChange = (value) => {
		updateState({ AAHandle: value, mobileNumber: '' });
	};

	const handleOTPChange = (value) => {
		updateState({ OTP: value });
	};

	const toggleSheet = () => {
		updateState({ isOpenSheet: !state.isOpenSheet });
	};

	const togglePrivacySheet = () => {
		updateState({ isOpenPrivacySheet: !state.isOpenPrivacySheet });
	};

	const toggleOTPSheet = () => {
		updateState({ isOpenOTPSheet: !state.isOpenOTPSheet });
	};

	// Computed values
	const isGetOTPDisabled =
		!(state.mobileNumber?.length === 10) && !state.AAHandle;
	const isVerifyDisabled =
		!(state.OTP?.length === 6 || state.OTP?.length === 8) ||
		state.verifySpinner;

	return (
		<div className="login-page mb-10" style={{
			width: isMobile ? '95%' : '40%',
			margin: '0 auto',
			marginTop:'6.5rem'
		  }}>
			<LoginForm
				customDetails={customDetails}
				mobileNumber={state.mobileNumber}
				AAHandle={state.AAHandle}
				sendSpinner={state.sendSpinner}
				isGetOTPDisabled={isGetOTPDisabled}
				onMobileNumberChange={handleMobileNumberChange}
				onAAHandleChange={handleAAHandleChange}
				onSendOtp={sendOtp}
				onToggleSheet={toggleSheet}
				onTogglePrivacySheet={togglePrivacySheet}
			/>

			<TermsModal
				isOpen={state.isOpenSheet}
				onClose={toggleSheet}
				sendSpinner={state.sendSpinner}
				customDetails={customDetails}
			/>

			<PrivacyPolicyModal
				isOpen={state.isOpenPrivacySheet}
				onClose={togglePrivacySheet}
				sendSpinner={state.sendSpinner}
				customDetails={customDetails}
			/>

			{isMobile ? (
				<OtpSheet
					isOpen={state.isOpenOTPSheet}
					onClose={toggleOTPSheet}
					customDetails={customDetails}
					mobileNumber={state.mobileNumber}
					OTP={state.OTP}
					time={state.time}
					isResend={state.isResend}
					verifySpinner={state.verifySpinner}
					isVerifyDisabled={isVerifyDisabled}
					onOTPChange={handleOTPChange}
					onResendOtp={sendOtp}
					onVerifyOtp={verifyOtp}
				/>
			) : (
				<OTPModal
					isOpen={state.isOpenOTPSheet}
					onClose={toggleOTPSheet}
					customDetails={customDetails}
					mobileNumber={state.mobileNumber}
					OTP={state.OTP}
					time={state.time}
					isResend={state.isResend}
					verifySpinner={state.verifySpinner}
					isVerifyDisabled={isVerifyDisabled}
					onOTPChange={handleOTPChange}
					onResendOtp={sendOtp}
					onVerifyOtp={verifyOtp}
				/>
			)}
		</div>
	);
};

export default Login;
